import React, { useRef, useEffect, useMemo } from 'react'
import ApexCharts from 'apexcharts'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Skeleton from '@mui/material/Skeleton'
import { OUTCOME_COLORS, OUTCOME_LABELS } from './channelColors'

export default function CallOutcomeChart({ data = {}, title = 'Call Outcome Distribution', height = 300, loading }) {
  /* data shape: { reattempt: 640, rto: 120, wrong_number: 85, escalation: 45, no_answer: 310, ... } */

  const chartRef = useRef(null)
  const chartInstance = useRef(null)

  const entries = useMemo(() =>
    Object.entries(data)
      .filter(([, v]) => v > 0)
      .sort(([, a], [, b]) => b - a),
    [data]
  )

  const options = useMemo(() => ({
    chart: { type: 'bar', height, toolbar: { show: false }, fontFamily: "'Inter', sans-serif" },
    plotOptions: {
      bar: { horizontal: true, barHeight: '60%', borderRadius: 4, distributed: true },
    },
    colors: entries.map(([k]) => OUTCOME_COLORS[k] || '#607D8B'),
    xaxis: {
      categories: entries.map(([k]) => OUTCOME_LABELS[k] || k),
      labels: {
        formatter: (v) => v >= 1000 ? `${(v / 1000).toFixed(1)}K` : v,
        style: { fontSize: '11px' },
      },
    },
    yaxis: { labels: { style: { fontSize: '12px', fontWeight: 600 } } },
    dataLabels: {
      enabled: true,
      formatter: (v) => v.toLocaleString('en-IN'),
      style: { fontSize: '11px', fontWeight: 700 },
      offsetX: 4,
    },
    legend: { show: false },
    grid: { borderColor: '#E0E4E8', strokeDashArray: 4, xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
    tooltip: { y: { formatter: (v) => v?.toLocaleString('en-IN') } },
  }), [entries, height])

  const chartSeries = useMemo(() => [{ name: 'Count', data: entries.map(([, v]) => v) }], [entries])

  useEffect(() => {
    if (!chartRef.current) return
    if (chartInstance.current) chartInstance.current.destroy()
    chartInstance.current = new ApexCharts(chartRef.current, { ...options, series: chartSeries })
    chartInstance.current.render()
    return () => { if (chartInstance.current) { chartInstance.current.destroy(); chartInstance.current = null } }
  }, [options, chartSeries])

  if (loading) {
    return (
      <Paper sx={{ p: 2.5 }}>
        <Skeleton width={200} height={22} sx={{ mb: 2 }} />
        <Skeleton variant='rectangular' height={height - 60} sx={{ borderRadius: 1 }} />
      </Paper>
    )
  }

  if (!entries.length) return null

  return (
    <Paper sx={{ p: 2.5 }}>
      <Typography variant='subtitle1' sx={{ mb: 1 }}>{title}</Typography>
      <div ref={chartRef} />
    </Paper>
  )
}
